import React, { type ComponentType } from 'react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button/standard-button';
import { ModalBody, ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import { JiraSoftwareBoardAri } from '@atlassian/ari/jira-software/board';
import ViewTracker from '@atlassian/jira-analytics-web-react/src/components/view-tracker.tsx';
import type { Board } from '@atlassian/jira-boards-common/src/model/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalDialog } from '@atlassian/jira-software-modal-dialog/src/ui/modal-dialog/index.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import { useDeleteBoard } from '../controllers/use-delete-board/index.tsx';
import type { SprintInformation } from '../model/index.tsx';
import messages from './messages.tsx';

export type Props = {
	board: Board;
	sprintInformation: SprintInformation | undefined;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Content: ComponentType<Record<any, any>>;
	deleteInProgress: boolean;
	onCancel: () => void;
	onDelete: () => void;
	intl: IntlShape;
};

function DeleteBoardsModal(props: Props): React.JSX.Element | null {
	const { Content, board, sprintInformation, deleteInProgress, onCancel, onDelete } = props;
	const {
		intl: { formatMessage },
	} = props;

	const tenantId = useCloudId();
	const { deleteBoard } = useDeleteBoard();

	if (!sprintInformation) {
		return null;
	}
	const onCancelOrNoop = !deleteInProgress ? onCancel : noop;

	// @ts-expect-error - TS2339 - Property 'isPrivate' does not exist on type 'Board'.
	const heading = board.isPrivate
		? formatMessage(messages.deletePrivateBoard)
		: // @ts-expect-error - TS2339 - Property 'name' does not exist on type 'Board'.
			formatMessage(messages.deleteBoard, { boardName: board.name });

	const handleDelete = () => {
		const boardARI = JiraSoftwareBoardAri.create({
			siteId: tenantId,
			boardId: board.id.toString() ?? '',
		}).toString();
		const dataId = RelayDataID({ id: boardARI }, 'JiraBoard');

		onDelete();
		if (dataId) {
			deleteBoard(dataId);
		}
	};

	return (
		<ShortcutScope>
			<ModalDialog
				messageId="delete-boards-modal.modal-dialog"
				messageType="transactional"
				onClose={onCancelOrNoop}
			>
				<ModalHeader hasCloseButton={fg('enghealth-modal-close-button-prevent-close-modal')}>
					<ModalTitle appearance="danger">{heading}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<Content />
					<ViewTracker />
				</ModalBody>
				<ModalFooter>
					<Button appearance="danger" onClick={handleDelete} isDisabled={deleteInProgress}>
						{formatMessage(messages.deleteCaption)}
					</Button>
					<Button appearance="subtle-link" onClick={onCancelOrNoop} isDisabled={deleteInProgress}>
						{formatMessage(messages.cancel)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
}

DeleteBoardsModal.defaultProps = {
	deleteInProgress: false,
	onCancel: noop,
	onDelete: noop,
};

export default DeleteBoardsModal;
